import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import EventInviteCard from '../components/EventInviteCard';
import FeatureIcon from '../components/FeatureIcon';
import UtilityPageFrame from '../components/utility/UtilityPageFrame';
import { sanitizedPageFields, trackEvent } from '../lib/analytics';
import type { EventPreview } from '../lib/eventPreview';

export type { EventPreview } from '../lib/eventPreview';

// Every state carries the request key it belongs to; render-time matching is
// what actually prevents a stale authorized view from surviving a param change.
type PageState =
  | { key: string; kind: 'loading' }
  | { key: string; kind: 'loaded'; event: EventPreview }
  | { key: string; kind: 'invalid' }
  | { key: string; kind: 'ended'; message: string }
  | { key: string; kind: 'rateLimited' }
  | { key: string; kind: 'error' };

// Minimal structural guard — a malformed payload becomes a safe generic error,
// never raw server detail.
const isEventPreview = (data: any): data is EventPreview =>
  !!data &&
  typeof data.id === 'string' &&
  typeof data.title === 'string' &&
  typeof data.isPublic === 'boolean' &&
  typeof data.participantCount === 'number';

const EventInvitePage: React.FC = () => {
  const params = useParams();
  // iOS generateEventLink emits uppercase UUIDs — normalize once so the
  // route id, fetch query, and DTO binding compare equal either way.
  const eventId = params.eventId?.toLowerCase();
  const [searchParams] = useSearchParams();

  const [state, setState] = useState<PageState>({ kind: 'loading', key: '' });
  const [reloadKey, setReloadKey] = useState(0);

  // Ephemeral credential: read for the request and dispatch targets only —
  // never rendered, logged, or sent to analytics. Parameter *presence* is what
  // routes the request — a blank `?code=` is still an invite attempt and
  // must fail invalid, never fall back to the public lane.
  const codeValues = searchParams.getAll('code');
  const duplicateCode = codeValues.length > 1;
  const hasCodeParam = codeValues.length >= 1;
  const inviteCode = hasCodeParam ? codeValues[0] ?? '' : undefined;
  // Ephemeral key — kept in memory, never rendered/logged. A duplicate code
  // param must invalidate the key so a prior authorized view cannot survive.
  const requestKey = `${eventId ?? ''}:${hasCodeParam ? `code:${duplicateCode ? 'dup' : inviteCode ?? ''}` : 'public'}`;
  const visibleState: PageState =
    state.key === requestKey ? state : { kind: 'loading', key: requestKey };
  const analyticsBase = {
    eventId,
    hasInviteCode: !!inviteCode,
    ...sanitizedPageFields(),
  };

  // Request key guards stale completions: a response is applied only while it
  // still belongs to the current eventId/code pair.
  const requestSeq = useRef(0);

  useEffect(() => {
    const seq = ++requestSeq.current;
    const controller = new AbortController();
    setState({ kind: 'loading', key: requestKey });
    // A new request invalidates the previous title — a private event's name
    // must never survive an invalid or unrelated link.
    document.title = 'Event invitation - RidePulse';

    if (!eventId) {
      trackEvent('invite_page_invalid_link', { ...analyticsBase, reason: 'missing_event_id' });
      setState({ kind: 'invalid', key: requestKey });
      return () => controller.abort();
    }

    // Duplicate invite-code params are ambiguous — the server rejects them
    // outright, and the SPA must too instead of silently picking one.
    if (duplicateCode) {
      trackEvent('invite_page_invalid_link', { ...analyticsBase, reason: 'duplicate_code' });
      setState({ kind: 'invalid', key: requestKey });
      return () => controller.abort();
    }

    trackEvent('invite_page_view', analyticsBase);

    const load = async () => {
      try {
        const qs = new URLSearchParams();
        qs.set('eventId', eventId);
        if (hasCodeParam) qs.set('code', inviteCode ?? '');

        const response = await fetch(
          `/.netlify/functions/event-preview?${qs.toString()}`,
          { signal: controller.signal }
        );
        const data = await response.json().catch(() => null);
        if (controller.signal.aborted || seq !== requestSeq.current) return;

        if (!response.ok) {
          trackEvent('invite_event_error', { ...analyticsBase, status: response.status });
          setState(
            response.status === 410
              ? { key: requestKey, kind: 'ended', message: 'This event has ended and the link is no longer available.' }
              : response.status === 429
                ? { key: requestKey, kind: 'rateLimited' }
                : response.status === 400 || response.status === 403 || response.status === 404
                  ? { key: requestKey, kind: 'invalid' }
                  : { key: requestKey, kind: 'error' }
          );
          return;
        }

        // The payload must structurally match AND belong to this event — a
        // mismatched id is treated as an error, never rendered.
        if (!isEventPreview(data) || data.id !== eventId) {
          trackEvent('invite_event_error', { ...analyticsBase, status: 'bad_payload' });
          setState({ kind: 'error', key: requestKey });
          return;
        }

        setState({ kind: 'loaded', key: requestKey, event: data });
        trackEvent('invite_event_loaded', { ...analyticsBase, isPublic: data.isPublic });
        if (data.title) document.title = `${data.title} - RidePulse`;
      } catch (err) {
        if ((err as Error)?.name === 'AbortError' || seq !== requestSeq.current) return;
        trackEvent('invite_event_error', { ...analyticsBase, status: 'network' });
        setState({ kind: 'error', key: requestKey });
      }
    };

    load();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, reloadKey]);

  const errorCard = (heading: string, body: string, retry: boolean) => (
    <div className="utility-card" style={{ textAlign: 'center' }}>
      <div aria-hidden="true" style={{ color: 'var(--rp-text-muted)', marginBottom: '0.75rem' }}>
        <FeatureIcon name="alert" size={36} />
      </div>
      <h1 className="utility-title utility-title--center" style={{ fontSize: '1.375rem' }}>{heading}</h1>
      <p className="utility-muted" role="status">{body}</p>
      {retry && (
        <button
          type="button"
          className="utility-button"
          onClick={() => setReloadKey((k) => k + 1)}
        >
          Try again
        </button>
      )}
      <p className="utility-muted" style={{ fontSize: '0.75rem', marginTop: '1rem' }}>
        Check your invite link or ask the host to resend it.
      </p>
    </div>
  );

  const eventFooter = (
    <nav aria-label="Event links" className="shared-event-footer">
      <Link to="/" className="utility-footer-link">Explore RidePulse</Link>
      <Link to="/contact" className="utility-footer-link">Contact support</Link>
      <Link to="/legal/privacy" className="utility-footer-link">Privacy</Link>
      <Link to="/legal/terms" className="utility-footer-link">Terms</Link>
    </nav>
  );

  return (
    <UtilityPageFrame wide footer={eventFooter}>
      {visibleState.kind === 'loading' && (
        <div className="utility-card" style={{ textAlign: 'center' }} role="status" aria-live="polite">
          <p className="utility-muted">Loading event…</p>
        </div>
      )}
      {visibleState.kind === 'invalid' &&
        errorCard(
          'Event link not valid',
          'This invite or event link could not be verified. It may be expired, revoked, or mistyped.',
          false
        )}
      {visibleState.kind === 'ended' && errorCard('Event no longer available', visibleState.message, false)}
      {visibleState.kind === 'rateLimited' &&
        errorCard(
          'Too many attempts',
          'Too many attempts. Please wait before trying again.',
          false
        )}
      {visibleState.kind === 'error' &&
        errorCard('Unable to load this event', 'A network or server problem occurred.', true)}
      {visibleState.kind === 'loaded' && (
        <EventInviteCard event={visibleState.event} inviteCode={inviteCode} />
      )}
    </UtilityPageFrame>
  );
};

export default EventInvitePage;
